import { useEffect, useRef, useState } from 'react'
import {
  api, unlock, type Child, type StartingCheck as CheckData, type StartingCheckAnswer, type StartingCheckResult,
} from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page, Progress } from '../components/ui'
import { Shell } from '../components/Shell'
import { ErrorScreen, Loading } from '../components/States'
import { Mascot } from '../components/Mascot'
import { GrownUpGate } from '../components/GrownUpGate'
import { say } from '../lib/speech'
import { sfx } from '../lib/sound'
import { fromServer, t } from '../lib/i18n'

/**
 * The starting-point check (beta). Sam's build list, item 10, 29 Oct 2026.
 *
 * A grown-up starts it from Settings and hands the tablet over. The child
 * hears a word and taps what it means: two practice words, then eighteen,
 * six a year below, six at and six a year above their school year
 * (research/placement-research/05-recommended-design.md). Every meaning can
 * be heard, so reading speed is not what is measured, and "I don't know" is
 * a fine answer. No right or wrong is shown after the practice: it is not a
 * test, and the child is never told a level.
 *
 * Then the tablet goes back to the grown-up (behind the grown-up PIN), who
 * sees how many were right in each group and the suggested start, and
 * chooses: that, one year easier, one harder, or no change.
 *
 * The child's part is in English, like every child screen; the grown-up's
 * part is in the family's language.
 */
type Phase = 'intro' | 'items' | 'sending' | 'handback' | 'gate' | 'result' | 'saved'

export function StartingCheck({ child, onDone }: { child: Child; onDone: () => void }) {
  const [check, setCheck] = useState<CheckData | null>(null)
  const [phase, setPhase] = useState<Phase>('intro')
  const [at, setAt] = useState(0)
  const [answers, setAnswers] = useState<StartingCheckAnswer[]>([])
  const [result, setResult] = useState<StartingCheckResult | null>(null)
  const [error, setError] = useState<unknown>(null)

  const load = () => {
    setError(null)
    api.startingCheck(child.learner_id).then((c) => {
      // The tablet is about to go to the child. Swap the grown-up's ticket
      // for one that opens only this child, so "I'm the grown-up" and the way
      // back to Settings ask for the PIN (Codex round 20, finding 3).
      if (c.ticket) {
        unlock.setChild(child.learner_id, c.ticket)
        unlock.dropGrownUp()
      }
      setCheck(c)
    }).catch(setError)
  }
  useEffect(load, [child.learner_id])

  const send = (all: StartingCheckAnswer[]) => {
    if (!check) return
    setPhase('sending')
    // The child's side gets back only "received": the counts are fetched
    // after the grown-up PIN (Codex round 16).
    api.finishStartingCheck(check.check_id, all)
      .then(() => setPhase('handback'))
      .catch(setError)
  }
  const showResult = () => {
    if (!check) return
    api.startingCheckResult(check.check_id)
      .then((r) => { setResult(r); setPhase('result') })
      .catch(setError)
  }

  if (error) {
    return <ErrorScreen error={error} onRetry={check ? () => send(answers) : load} onHome={onDone} grownUp />
  }
  if (!check || phase === 'sending') return <Loading what={check ? 'Saving' : 'Getting the words ready'} />

  const items = check.items
  const scored = items.filter((i) => !i.practice).length

  if (phase === 'intro') {
    return (
      <Page width={560}>
        <Shell exitMode="home" onExit={onDone}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <Mascot size={72} mood="happy" label="" />
            <Heading sub="This is not a school test. You can change it later.">
              Let's find a good place to start
            </Heading>
          </div>
          <Card>
            <p style={{ margin: '0 0 10px', fontSize: 17, lineHeight: 1.55, color: theme.text }}>
              You will hear a word. Tap what it means.
            </p>
            <p style={{ margin: '0 0 10px', fontSize: 17, lineHeight: 1.55, color: theme.text }}>
              Tap 🔊 to hear any of them again.
            </p>
            <p style={{ margin: 0, fontSize: 17, lineHeight: 1.55, color: theme.text }}>
              Some words are hard on purpose. If you don't know one, tap <b>I don't know</b>. That is a good answer.
            </p>
          </Card>
          <div style={{ marginTop: 18 }}>
            <Button full tone="child" onClick={() => { sfx.tap(); setPhase('items') }}>Start</Button>
          </div>
        </Shell>
      </Page>
    )
  }

  if (phase === 'items') {
    const item = items[at]!
    const doneScored = items.slice(0, at).filter((i) => !i.practice).length
    return (
      <Page width={560}>
        <Shell exitMode="leave" onExit={onDone}
          step={item.practice ? 'Practice' : `${doneScored + 1} of ${scored}`}>
          <div style={{ marginBottom: 18 }}>
            <Progress done={item.practice ? 0 : doneScored} total={scored} />
          </div>
          <CheckItem
            key={item.index}
            word={item.word}
            options={item.options}
            practiceAnswer={item.practice ? item.answer_index ?? null : null}
            onAnswer={(choice, ms, replays) => {
              const next = [...answers, { index: item.index, choice, ms, replays }]
              setAnswers(next)
              if (at + 1 < items.length) setAt(at + 1)
              else send(next)
            }}
          />
        </Shell>
      </Page>
    )
  }

  if (phase === 'handback') {
    return (
      <Page width={560}>
        <Shell exitMode="done" onExit={() => setPhase('gate')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Mascot size={84} mood="cheer" label="" />
            <Heading sub="Please give the tablet back to a grown-up.">All done. Thank you, {child.display_name}!</Heading>
          </div>
          <div style={{ marginTop: 18 }}>
            <Button full onClick={() => setPhase('gate')}>{t("I'm the grown-up")}</Button>
          </div>
        </Shell>
      </Page>
    )
  }

  if (phase === 'gate') {
    return (
      <Page width={420}>
        <GrownUpGate why={t('To see the starting-point check.')}
          onPass={showResult} onCancel={() => setPhase('handback')} />
      </Page>
    )
  }

  if (result && (phase === 'result' || phase === 'saved')) {
    return <ResultScreen child={child} result={result} saved={phase === 'saved'}
      onChosen={(r) => { setResult(r); setPhase('saved') }} onDone={onDone} />
  }
  return <Loading />
}

/** One word, four meanings, and "I don't know". */
function CheckItem({
  word, options, practiceAnswer, onAnswer,
}: {
  word: string
  options: string[]
  /** The practice items show how it works; the scored ones show nothing. */
  practiceAnswer: number | null
  onAnswer: (choice: number | null, ms: number, replays: number) => void
}) {
  const shownAt = useRef(Date.now())
  const [replays, setReplays] = useState(0)
  const [picked, setPicked] = useState<number | null | undefined>(undefined)

  // The word is always spoken (research file 05: reading is not what is measured).
  useEffect(() => { say(word) }, [word])

  const pick = (choice: number | null) => {
    if (picked !== undefined) return
    sfx.tap()
    const ms = Date.now() - shownAt.current
    if (practiceAnswer === null) { onAnswer(choice, ms, replays); return }
    setPicked(choice)
    if (choice === practiceAnswer) sfx.correct()
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 34, fontWeight: 800, color: theme.text }}>{word}</div>
        {/* Once more, then the button rests: research file 05 allows one replay. */}
        <button type="button" disabled={replays >= 1}
          onClick={() => { setReplays((r) => r + 1); say(word) }}
          aria-label={`Hear ${word} again`}
          style={{
            fontFamily: 'inherit', fontSize: 18, minHeight: TAP_MIN, minWidth: TAP_MIN,
            borderRadius: radius.sm, border: `1.5px solid ${theme.border}`, background: theme.card,
            cursor: replays >= 1 ? 'default' : 'pointer', opacity: replays >= 1 ? 0.5 : 1,
          }}>🔊</button>
      </div>
      <div style={{ fontSize: 16, color: theme.muted, marginBottom: 10 }}>Which one does it mean?</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map((text, i) => {
          const showRight = picked !== undefined && i === practiceAnswer
          const showMine = picked === i && i !== practiceAnswer
          return (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <button type="button" onClick={() => pick(i)} style={{
                flex: 1, textAlign: 'left', fontFamily: 'inherit', fontSize: 17, lineHeight: 1.4,
                padding: '12px 14px', minHeight: TAP_MIN, borderRadius: radius.md, cursor: 'pointer',
                color: theme.text,
                background: showRight ? theme.greenPale : showMine ? theme.orangePale : theme.card,
                border: `2px solid ${showRight ? theme.green : showMine ? theme.orange : theme.borderSoft}`,
              }}>{text}</button>
              <button type="button" onClick={() => say(text)} aria-label="Hear this one"
                style={{
                  fontFamily: 'inherit', fontSize: 16, minWidth: TAP_MIN, borderRadius: radius.md,
                  border: `1.5px solid ${theme.border}`, background: theme.card, cursor: 'pointer',
                }}>🔊</button>
            </div>
          )
        })}
      </div>
      {picked === undefined ? (
        <div style={{ marginTop: 14 }}>
          <Button tone="quiet" onClick={() => pick(null)}>I don't know</Button>
        </div>
      ) : (
        <Card style={{ marginTop: 14, background: theme.greenPale, borderColor: theme.green }}>
          <p style={{ margin: '0 0 12px', fontSize: 17, color: theme.text }}>
            {picked === practiceAnswer
              ? 'Yes, that is it. That is how it works.'
              : picked === null
                ? `"I don't know" is fine. It means: ${options[practiceAnswer ?? 0]}`
                : `This time it means: ${options[practiceAnswer ?? 0]}`}
          </p>
          <Button full tone="child"
            onClick={() => onAnswer(picked ?? null, Date.now() - shownAt.current, replays)}>
            Next
          </Button>
        </Card>
      )}
    </div>
  )
}

const yearName = (y: number) => (y <= 0 ? t('kindergarten') : t('year {n}', { n: y }))

/** What the grown-up sees, and their choice. Counts, never a score or an age. */
function ResultScreen({
  child, result, saved, onChosen, onDone,
}: {
  child: Child
  result: StartingCheckResult
  saved: boolean
  onChosen: (r: StartingCheckResult) => void
  onDone: () => void
}) {
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const school = result.school_year
  const suggested = result.suggested_offset ?? 0
  const current = result.current_offset ?? child.year_offset

  const choose = async (offset: number) => {
    setBusy(true)
    setProblem(null)
    try {
      onChosen(await api.chooseStart(result.check_id, offset))
    } catch (err) {
      setProblem(fromServer(err, 'That did not save.'))
    } finally {
      setBusy(false)
    }
  }

  const rows: [string, number | null][] = [
    [t('A year below ({year})', { year: yearName(school - 1) }), result.lower_right],
    [t('Their school year ({year})', { year: yearName(school) }), result.middle_right],
    [t('A year above ({year})', { year: yearName(school + 1) }), result.upper_right],
  ]
  const options = [suggested - 1, suggested, suggested + 1].filter((o) => o >= -3 && o <= 3)

  return (
    <Page width={560}>
      <Shell exitMode="done" onExit={onDone} grownUp>
        <Heading sub={t('Beta: new, and not yet tested with many children. It only picks a place to start.')}>
          {t("{name}'s starting-point check", { name: child.display_name })}
        </Heading>

        <Card>
          {rows.map(([label, n]) => (
            <div key={label} style={{
              display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0',
              borderBottom: `1px solid ${theme.borderSoft}`, fontSize: 16, color: theme.text,
            }}>
              <span>{label}</span>
              <b>{t('{n} of {total} right', { n: n ?? 0, total: result.per_group })}</b>
            </div>
          ))}
          <p style={{ margin: '14px 0 0', fontSize: 17, color: theme.text, lineHeight: 1.5 }}>
            {t('Suggested start: {year} words.', { year: yearName(school + suggested) })}
          </p>
          {result.confidence === 'low' ? (
            <p style={{ margin: '8px 0 0', fontSize: 15, color: theme.orangeDark, lineHeight: 1.5 }}>
              {t('The answers did not agree with each other, so this suggestion is less sure. Starting at the school year is the safer choice.')}
            </p>
          ) : null}
        </Card>

        {saved ? (
          <Card style={{ marginTop: 14, background: theme.greenPale, borderColor: theme.green }}>
            <p style={{ margin: '0 0 12px', fontSize: 16, color: theme.text, lineHeight: 1.5 }}>
              {t('Saved: {year} words. New words will move to this level over the next few practices. Settings suggests a change if they turn out too easy or too hard.',
                { year: yearName(school + (result.chosen_offset ?? 0)) })}
            </p>
            <Button full onClick={onDone}>{t('Done')}</Button>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            {options.map((o) => (
              <Button key={o} full tone={o === suggested ? 'primary' : 'quiet'} disabled={busy}
                onClick={() => void choose(o)}>
                {o === suggested
                  ? t('Use the suggestion: {year}', { year: yearName(school + o) })
                  : o < suggested
                    ? t('One year easier: {year}', { year: yearName(school + o) })
                    : t('One year harder: {year}', { year: yearName(school + o) })}
              </Button>
            ))}
            {!options.includes(current) ? (
              <Button full tone="ghost" disabled={busy} onClick={onDone}>
                {t('Keep what it is now: {year}', { year: yearName(school + current) })}
              </Button>
            ) : null}
            {problem ? <p style={{ margin: 0, color: theme.red, fontSize: 14 }}>{problem}</p> : null}
          </div>
        )}
      </Shell>
    </Page>
  )
}
